import { useEffect, useRef, useState } from "react";
import { createPortal, flushSync } from "react-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Sun, Moon } from "lucide-react";
import { applyThemeMode } from "../lib/themeMode";
import { useT } from "../i18n/I18nProvider";

const ICON_TRANSITION = "opacity .32s cubic-bezier(0.4,0,0.2,1), transform .32s cubic-bezier(0.4,0,0.2,1)";

/** Reveal duration, kept in sync with the `::view-transition-new(root)` rule in index.css. */
const REVEAL_MS = 520;

interface Reveal {
  cx: number;
  cy: number;
  diameter: number;
  bg: string;
  next: boolean;
}

interface ViewTransition {
  ready: Promise<void>;
  finished: Promise<void>;
}

/** Id of the <style> element that carries the current reveal origin. */
const REVEAL_STYLE_ID = "wa-theme-reveal-origin";

/**
 * Arm the reveal so the circle grows out of the pressed button.
 *
 * Two deliberate choices, both of which were bugs before:
 *
 * - The origin is written as LITERAL values into a <style> element rather than into
 *   custom properties on <html>. Nothing then depends on custom properties
 *   inheriting into the ::view-transition pseudo tree, or on some other code path
 *   rewriting the root element's inline style mid-transition.
 * - The origin is expressed in PERCENTAGES of the pseudo-element's own box, not in
 *   viewport px. `::view-transition-new(root)` is laid out in the snapshot
 *   containing block, which is not guaranteed to match the viewport 1:1; px
 *   coordinates taken from getBoundingClientRect() then land somewhere else and the
 *   circle appears to grow from the wrong place. A percentage tracks the button
 *   whatever that box turns out to be.
 *
 * Must run BEFORE startViewTransition so the transition's very first frame is
 * already clipped to a zero-radius circle at the button.
 */
function armReveal(cx: number, cy: number, radius: number) {
  const w = window.innerWidth;
  const h = window.innerHeight;
  const x = (cx / w) * 100;
  const y = (cy / h) * 100;
  // A percentage radius resolves against sqrt(w^2 + h^2) / sqrt(2) of the box.
  const r = (radius / (Math.hypot(w, h) / Math.SQRT2)) * 100;
  let el = document.getElementById(REVEAL_STYLE_ID) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement("style");
    el.id = REVEAL_STYLE_ID;
  }
  const at = `${x.toFixed(3)}% ${y.toFixed(3)}%`;
  el.textContent =
    `@media (prefers-reduced-motion: no-preference){` +
    `@keyframes wa-theme-reveal-origin{` +
    `from{clip-path:circle(0% at ${at})}` +
    `to{clip-path:circle(${r.toFixed(3)}% at ${at})}}` +
    `::view-transition-new(root){` +
    `animation:wa-theme-reveal-origin ${REVEAL_MS}ms cubic-bezier(0.4,0,0.2,1) both !important}}`;
  // Re-append so the rule stays last in the cascade even if the app injected CSS later.
  document.head.appendChild(el);
}

/**
 * Theme toggle pill (light<->dark) with a circular "ink" transition - the same
 * growing-circle animation as the public front-end (web_site ThemeToggle).
 *
 * - With the View Transitions API we reveal the *real* content of the new theme
 *   as a circle growing out of the button. The circle is a CSS animation on
 *   ::view-transition-new(root) (index.css); this component only feeds it the
 *   origin and radius.
 * - Older browsers fall back to a framer-motion full-screen overlay.
 * - The sun/moon icon morphs with a CSS transition, and a small "ripple" ring
 *   spreads out of the button.
 * - With `prefers-reduced-motion` the theme flips immediately, with no animation.
 *
 * A press stores an explicit light/dark choice (`applyThemeMode` -> localStorage +
 * the `.dark` class on <html>), leaving "system" mode behind.
 */
export function ThemeToggle() {
  const t = useT();
  const [dark, setDark] = useState(false);
  const [reveal, setReveal] = useState<Reveal | null>(null);
  const [pulseKey, setPulseKey] = useState(0);
  const [mounted, setMounted] = useState(false);
  // While a view transition runs the live DOM is hidden behind static snapshots,
  // so the icon cross-fade would be invisible and then pop at the end. Swap the
  // icon instantly instead: the growing circle does the cross-fade for us.
  const [instantIcon, setInstantIcon] = useState(false);
  const reduce = useReducedMotion();
  const busy = useRef(false);

  // Sync the button state with the actual <html class="dark"> (avoids hydration flicker)
  useEffect(() => {
    setDark(document.documentElement.classList.contains("dark"));
    setMounted(true);
  }, []);

  function applyTheme(next: boolean) {
    applyThemeMode(next ? "dark" : "light");
    setDark(next);
  }

  function toggleMode(e: React.MouseEvent<HTMLButtonElement>) {
    if (busy.current) return;
    const html = document.documentElement;
    const next = !html.classList.contains("dark");

    setPulseKey((k) => k + 1);

    // Motion-sensitive users -> flip immediately.
    if (reduce) { applyTheme(next); return; }

    const r = e.currentTarget.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const endRadius = Math.hypot(
      Math.max(cx, window.innerWidth - cx),
      Math.max(cy, window.innerHeight - cy),
    );

    // Preferred path: View Transitions -> a circle revealing the real content.
    const startViewTransition = (document as unknown as {
      startViewTransition?: (cb: () => void) => ViewTransition;
    }).startViewTransition;

    // A hidden document skips the transition outright, so do not pay for it.
    if (typeof startViewTransition === "function") {
      if (document.visibilityState !== "visible") { applyTheme(next); return; }
      busy.current = true;
      armReveal(cx, cy, endRadius);
      const vt = startViewTransition.call(document, () => {
        // flushSync: the browser snapshots the new state as soon as this callback
        // returns. Without it React commits a tick later and the snapshot still
        // shows the old UI, which then jumps into place when the transition ends.
        flushSync(() => {
          setInstantIcon(true);
          applyTheme(next);
        });
      });
      // Release only when the transition is really over. Releasing on `ready`
      // let a second click start a transition on top of the first one, which the
      // browser resolves by skipping the first -> a visible snap.
      // `finished` rejects when the transition is skipped, so handle both.
      const done = () => { busy.current = false; setInstantIcon(false); };
      vt.finished.then(done, done);
      return;
    }

    // Fallback path: measure the new theme's background color (synchronously,
    // without painting), then let the framer-motion circle ink it in.
    html.classList.toggle("dark", next);
    const bg = getComputedStyle(html).getPropertyValue("--color-background").trim() || (next ? "#0b0b0c" : "#ffffff");
    html.classList.toggle("dark", !next); // restore the current state until the circle covers the screen

    setReveal({ cx, cy, diameter: endRadius * 2, bg, next });
  }

  const ariaLabel = t("preferences.theme");
  const title = dark ? t("preferences.themeLight") : t("preferences.themeDark");
  const iconTransition = mounted && !reduce && !instantIcon ? ICON_TRANSITION : "none";

  return (
    <>
      <button
        type="button"
        onClick={toggleMode}
        aria-label={ariaLabel}
        title={title}
        className="relative inline-flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-transparent text-foreground transition-colors hover:bg-accent"
      >
        {/* "Ripple" - a ring spreading out of the button on every press (keyed remount). */}
        {!reduce && pulseKey > 0 && (
          <motion.span
            key={pulseKey}
            initial={{ scale: 0.4, opacity: 0.45 }}
            animate={{ scale: 2.4, opacity: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: 999,
              background: "currentColor",
              pointerEvents: "none",
            }}
          />
        )}

        {/* Sun/moon - both always in the DOM, cross-fading on `dark`. The transition
            is enabled only after mount -> no animation on first load. */}
        <span style={{ position: "relative", display: "inline-flex", width: 16, height: 16 }}>
          <span
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              display: "inline-flex",
              opacity: dark ? 1 : 0,
              transform: dark ? "rotate(0deg) scale(1)" : "rotate(-90deg) scale(0)",
              transition: iconTransition,
            }}
          >
            <Sun className="size-4" />
          </span>
          <span
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              display: "inline-flex",
              opacity: dark ? 0 : 1,
              transform: dark ? "rotate(90deg) scale(0)" : "rotate(0deg) scale(1)",
              transition: iconTransition,
            }}
          >
            <Moon className="size-4" />
          </span>
        </span>
      </button>

      {/* Fallback overlay (only when View Transitions are missing). Portalled into body. */}
      {mounted && reveal && createPortal(
        <motion.div
          style={{ position: "fixed", inset: 0, zIndex: 9999, pointerEvents: "none" }}
          aria-hidden
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: REVEAL_MS / 1000, ease: [0.4, 0, 0.2, 1] }}
            onAnimationComplete={() => {
              applyTheme(reveal.next);
              setReveal(null);
            }}
            style={{
              position: "absolute",
              left: reveal.cx,
              top: reveal.cy,
              width: reveal.diameter,
              height: reveal.diameter,
              marginLeft: -reveal.diameter / 2,
              marginTop: -reveal.diameter / 2,
              borderRadius: "50%",
              background: reveal.bg,
            }}
          />
        </motion.div>,
        document.body,
      )}
    </>
  );
}
